// BookingTemplate — interactive. Calendar grid of available slots.
// For now: hardcoded slots based on the existing server config
// (Slots: days=[1,3,5] hours_IST=[14,16] horizon=14d).
// Plan B will hook to the real /api/concierge/v1/slots endpoint.

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Orb } from '../stage/Orb';
import { Eyebrow } from '../stage/Eyebrow';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface BookingProps {
  content: SceneContent;
}

interface Slot {
  iso: string;
  dayLabel: string;
  hourLabel: string;
}

// Generate the next 14 days, only Mon/Wed/Fri (1, 3, 5), at 14:00 / 16:00 IST.
function generateSlots(): Slot[] {
  const slots: Slot[] = [];
  const now = new Date();
  for (let i = 1; i < 15; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    const dow = d.getDay();
    if (![1, 3, 5].includes(dow)) continue;
    for (const hour of [14, 16]) {
      const slot = new Date(d);
      slot.setHours(hour, 0, 0, 0);
      slots.push({
        iso: slot.toISOString(),
        dayLabel: slot.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }),
        hourLabel: `${hour}:00 IST`,
      });
    }
  }
  return slots.slice(0, 8); // cap at 8 displayed
}

export function BookingTemplate({ content }: BookingProps) {
  const { headline, body, eyebrow } = content;
  const [selected, setSelected] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const slots = generateSlots();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !email.includes('@')) return;
    // Stub: real submission goes to /api/concierge/v1/booking when Plan B lands.
    setSubmitted(true);
  };

  return (
    <Stage>
      {/* Orb anchor — bottom-right, small */}
      <div className="absolute bottom-12 right-12 max-md:bottom-6 max-md:right-6">
        <Orb size={40} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.1)}
        className="m-auto w-full max-w-[1100px] flex flex-col gap-10"
      >
        <div className="text-center flex flex-col gap-6 items-center">
          {eyebrow && (
            <motion.div variants={fadeUp}>
              <Eyebrow>{eyebrow}</Eyebrow>
            </motion.div>
          )}
          {headline && (
            <motion.h2 variants={fadeUp} className="text-headline font-display max-w-[28ch] text-balance">
              {headline}
            </motion.h2>
          )}
          {body && (
            <motion.p variants={fadeUp} className="text-body text-fg-soft max-w-[52ch]">
              {body}
            </motion.p>
          )}
        </div>

        {!submitted ? (
          <>
            <motion.div
              variants={stagger(0.05)}
              className="grid grid-cols-4 max-md:grid-cols-2 gap-3"
            >
              {slots.map((s) => {
                const isSelected = selected === s.iso;
                return (
                  <motion.button
                    key={s.iso}
                    variants={fadeUp}
                    onClick={() => setSelected(s.iso)}
                    className={`px-4 py-4 rounded-xl border text-left flex flex-col gap-1 transition-colors ${
                      isSelected
                        ? 'border-accent bg-accent/10 text-fg'
                        : 'border-fg-faint/40 bg-void-800/60 text-fg-soft hover:border-fg-faint hover:bg-void-700/80'
                    }`}
                  >
                    <span className="text-eyebrow uppercase font-medium tracking-[0.18em]">
                      {s.dayLabel}
                    </span>
                    <span className="text-body font-display font-medium">
                      {s.hourLabel}
                    </span>
                  </motion.button>
                );
              })}
            </motion.div>

            <AnimatePresence>
              {selected && (
                <motion.form
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  onSubmit={handleSubmit}
                  className="flex flex-col sm:flex-row gap-3 mt-2 max-w-[640px] mx-auto w-full"
                >
                  <input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-5 py-4 rounded-xl bg-void-800/80 border border-fg-faint/50 text-body text-fg placeholder:text-fg-mute focus:outline-none focus:border-accent/60 font-display"
                  />
                  <button
                    type="submit"
                    disabled={!email.includes('@')}
                    className="px-6 py-4 rounded-xl bg-accent text-void-900 font-display font-semibold text-body hover:bg-accent-glow disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Confirm
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 text-center flex flex-col gap-4 items-center"
          >
            <span className="text-display-2 font-display text-accent">✓</span>
            <h3 className="text-headline font-display">Booked.</h3>
            <p className="text-body text-fg-soft max-w-[44ch]">
              You'll get a calendar invite at <span className="text-fg font-mono">{email}</span>.
              <br />
              RG will join from a quiet room.
            </p>
          </motion.div>
        )}
      </motion.div>
    </Stage>
  );
}
